import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import {
  ReviewDoubtLock,
  ReviewExtractControls,
} from '@/components/admin/sessions/review/review-extract-controls';
import { DISABLED_INERT } from '@/components/ui/button';
import { type PassageTarget, doubtState, otherCases } from '@/lib/review-gestures';
import { caseNumber } from '@/lib/review-transcript';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

/**
 * The popover of a passage already ranged (relecture.md §5.3, D24): a line per
 * case that holds it - the pastille, the NAME of the case, which leads to it, then
 * the same « ? » and the same cross as the extract carries in its case, aligned in
 * a column - and under them « Ranger aussi dans », the other cases.
 *
 * A ranging is removed by the cross ONLY; changing case is ranging elsewhere,
 * then removing. Nothing here moves a passage, and clicking a name never
 * removes anything.
 */

export interface ReviewPlacedListProps {
  readonly ids: readonly string[];
  /** The name of a case was pressed: lead to it. */
  readonly onGoTo: (objectiveId: string) => void;
  /** « Ranger aussi dans » a case. */
  readonly onPick: (objectiveId: string, target: PassageTarget) => void;
  /** Every extract under the mark is gone: nothing left to show. */
  readonly onEmpty: () => void;
}

export function ReviewPlacedList({ ids, onGoTo, onPick, onEmpty }: ReviewPlacedListProps) {
  const { review, grid, order, canWrite, busy } = useReviewApi();
  const extracts = review.extracts.filter((extract) => ids.includes(extract.id));
  const first = extracts[0];

  useEffect(() => {
    if (first === undefined) onEmpty();
  }, [first, onEmpty]);

  if (first === undefined || grid === null) return null;
  const target: PassageTarget = {
    turn: first.turn,
    start: first.start,
    end: first.end,
    text: first.text,
  };
  const others = otherCases(grid, extracts);

  return (
    <>
      <p className="text-gray-600">Ce passage est rangé en :</p>
      <div className="flex flex-col gap-1">
        {extracts.map((extract) => {
          const doubt = doubtState(extract, review.extracts, grid);
          const number = caseNumber(order, extract.objectiveId);
          const name = grid.objectives[number - 1]?.name ?? extract.objectiveId;
          return (
            <div
              key={extract.id}
              data-testid={REVIEW_TEST_IDS.placed}
              data-extract={extract.id}
              className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 rounded-md py-1 pr-1 pl-1.5"
            >
              <span
                aria-hidden="true"
                className="inline-flex size-[22px] items-center justify-center rounded-pill bg-bleu-nuit font-bold text-white text-xs"
              >
                {number}
              </span>
              <button
                type="button"
                data-testid={REVIEW_TEST_IDS.goto}
                className="cursor-pointer text-left font-semibold text-bleu-nuit hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onGoTo(extract.objectiveId)}
              >
                {name}
              </button>
              <span className="justify-self-end">
                <ReviewExtractControls extract={extract} doubt={doubt} />
              </span>
              {doubt.locked ? (
                <span className="col-span-3">
                  <ReviewDoubtLock doubt={doubt} />
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {canWrite && others.length > 0 ? (
        <>
          <p className="border-gray-100 border-t pt-1.5 font-semibold text-gray-600 text-xs">
            Ranger aussi dans
            {grid.oneObjectivePerPassage ? ', avec doute sur les deux placements' : ''} :
          </p>
          <div className="flex flex-col gap-0.5">
            {others.map((other) => (
              <button
                key={other.objectiveId}
                type="button"
                data-testid={REVIEW_TEST_IDS.option}
                data-objective={other.objectiveId}
                disabled={busy}
                onClick={() => onPick(other.objectiveId, target)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-bleu-nuit-tint focus-visible:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  busy && `cursor-not-allowed ${DISABLED_INERT}`,
                )}
              >
                <span
                  aria-hidden="true"
                  className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-pill bg-bleu-nuit font-bold text-white text-xs"
                >
                  {other.number}
                </span>
                {other.name}
              </button>
            ))}
          </div>
        </>
      ) : null}
      {canWrite ? (
        <p className="text-gray-500 text-xs">
          Le nom mène à la case ; la croix retire le rangement. Pour changer de case, rangez
          ailleurs puis retirez avec la croix.
        </p>
      ) : null}
    </>
  );
}
